import React from "react";
import { useTranslation } from "react-i18next";
import {
  Loader,
  CxmDepositForm,
  CxmRouteLoader,
  DepositForm,
  PageLayout,
  AlfredIneligibleBlocker,
  AlfredKycFallbackForm,
} from "src/components";
import { useDepositWithdrawContext, useUrlParamsContext } from "src/contexts";
import {
  useAlfredKycFallbackGate,
  useCxmDeposit,
  useCxmLoaderSequence,
  usePaymentsRoutes,
} from "src/hooks";
import { isCxmDirectRoute, isCxmExchange } from "src/api";
import "./deposit.scss";

/**
 * CXM deposit page. Shares no provider logic with the Alfred page: Alfred's
 * eligibility verdict and KYC-fallback gate are answers about an *Alfred*
 * customer, and on a CXM tenant Alfred is not configured at all.
 *
 * Splitting the pages is what actually keeps them apart: hooks cannot be
 * called conditionally, so branching inside one component would still run
 * Alfred's gate on every CXM render.
 */
const CxmDepositPage: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const { widgetDataLoading, widgetDataError } = useDepositWithdrawContext();
  // The page owns the orchestrator so the pane can wait for the method
  // catalogue too — otherwise the glass clears onto a form whose method
  // select is still empty. It also starts that request alongside the
  // widget-data one instead of after it.
  const deposit = useCxmDeposit();

  const loading = widgetDataLoading || deposit.methodsLoading || Boolean(widgetDataError);
  const loader = useCxmLoaderSequence(loading);

  return (
    <>
      {!loading && loader.revealed && (
        <PageLayout centered>
          <CxmDepositForm deposit={deposit} />
        </PageLayout>
      )}
      {loader.phase !== "done" && (
        <CxmRouteLoader label={t("CXM.DEPOSIT.LOADING")} leaving={loader.phase === "leaving"} />
      )}
    </>
  );
};

/** Everything that is not CXM — unchanged, Alfred gates and fades included. */
const AlfredDepositPage: React.FC = (): JSX.Element => {
  const { widgetDataLoading, widgetDataError, alfredIneligibilityCode } =
    useDepositWithdrawContext();
  const { alfredKycFallback, loading: kycFallbackLoading } =
    useAlfredKycFallbackGate(alfredIneligibilityCode);

  // Resolve the active screen + a stable key. The key drives the wrapper's
  // remount so every screen swap fades in uniformly (not just the Alfred ones).
  let screen: JSX.Element;
  let screenKey: string;
  if (widgetDataLoading || widgetDataError || kycFallbackLoading) {
    screen = <Loader />;
    screenKey = "loading";
  } else if (alfredKycFallback) {
    screen = <AlfredKycFallbackForm fallback={alfredKycFallback} />;
    screenKey = "kyc-fallback";
  } else if (alfredIneligibilityCode) {
    screen = <AlfredIneligibleBlocker code={alfredIneligibilityCode} />;
    screenKey = "ineligible";
  } else {
    screen = (
      <PageLayout>
        <DepositForm />
      </PageLayout>
    );
    screenKey = "form";
  }

  return (
    <div key={screenKey} className="deposit-screen">
      {screen}
    </div>
  );
};

/** Same reasoning as the withdrawal page: the route names the provider, not
 *  the tenant. See `pages/withdrawal/Withdrawal.tsx`. */
const DepositPage: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const { exchange } = useUrlParamsContext();
  const { paymentsRoutes, paymentsRoutesLoading } = usePaymentsRoutes();

  if (paymentsRoutesLoading) {
    return isCxmExchange(exchange) ? (
      <CxmRouteLoader label={t("CXM.DEPOSIT.LOADING")} />
    ) : (
      <Loader />
    );
  }

  return isCxmDirectRoute(paymentsRoutes.psp_service_id) ? (
    <CxmDepositPage />
  ) : (
    <AlfredDepositPage />
  );
};

export default DepositPage;
